import { yupResolver } from '@hookform/resolvers/yup'
import type { DateRange, ITravelerInfo } from '@pcln/horizon'
import {
  Button,
  DatePicker,
  Span,
  TRANSFORMER_MAX_MOBILE_WIDTH_PX,
  TravelerSelection,
  cn,
} from '@pcln/horizon'
import { addDays } from 'date-fns/addDays'
import { addMonths } from 'date-fns/addMonths'
import { format } from 'date-fns/format'
import { parse } from 'date-fns/parse'
import { useCallback, useState } from 'react'
import { useForm } from 'react-hook-form'

import AutocompleteWrapper from '@/components/AutocompleteWrapper/AutocompleteWrapper'
import { DEFAULT_COLLISION_AVOIDANCE } from '@/components/SearchForm/constants'
import useTabGroup from '@/hooks/useTabGroup'
import logSearchFormAnalytics from '@/shared-utils/log-search-form-analytics'

import type { LOCATION_SEARCH_TYPE, TravelerState } from '@/types'
import { useMediaQuery } from 'react-responsive'
import type { HotelDetail, MultiHotelFormState } from '../../SearchForm/MultiHotelSearchForm/types'
import {
  addDestination,
  handleSubmitForm,
  removeDestination,
  setHotels,
} from '../../SearchForm/MultiHotelSearchForm/utils'
import { searchFormSchema } from '../../SearchForm/MultiHotelSearchForm/validation'
import { getFormContainerClass, getGridContainerClass, getInputClass } from './HorizonMultiHotelForm.variants'
import type { MultiHotelInitialData } from './types'
import useMultiHotelFormInitialValues from './useMultiHotelFormInitialValues'
import useMultiHotelFormSubmitValues from './useMultiHotelFormSubmitValues'

type MultiHotelFormProps = {
  pageName?: 'homepage' | 'landing'
  initialData?: MultiHotelInitialData
  useCompactLayout?: boolean
}

export default function MultiHotelForm({
  pageName = 'homepage',
  initialData,
  useCompactLayout = false,
}: MultiHotelFormProps) {
  const { registerTabElement } = useTabGroup()
  const { initialValues, isMobile, cguid } = useMultiHotelFormInitialValues(initialData)
  const [activeHotelState, setActiveHotelState] = useState<{
    [hotelId: number]: {
      isSelectingStart: boolean
      isSelectingEnd: boolean
      tempStartDate?: Date
      tempEndDate?: Date
    }
  }>({})

  const {
    clearErrors,
    control,
    setValue,
    handleSubmit,
    watch,
    trigger,
    formState: { errors },
  } = useForm<MultiHotelFormState>({
    defaultValues: initialValues,
    mode: 'onSubmit',
    resolver: yupResolver(searchFormSchema),
    reValidateMode: 'onChange',
  })

  const isMobileComponent = useMediaQuery({ maxWidth: TRANSFORMER_MAX_MOBILE_WIDTH_PX })
  const { watchHotels } = useMultiHotelFormSubmitValues({
    control,
    watch,
    clearErrors,
    trigger,
    initialValues,
  })

  const onCalendarChange = useCallback(
    (dates: DateRange, index: number): void => {
      const hotel = watchHotels[index]
      const hotelId = hotel.id

      // Initialize hotel state if it doesn't exist
      if (!activeHotelState[hotelId]) {
        setActiveHotelState((prev) => ({
          ...prev,
          [hotelId]: {
            isSelectingStart: true,
            isSelectingEnd: false,
          },
        }))
      }

      if (dates?.from) {
        // If we have both from and to dates, and they're different, it's a complete range
        if (dates?.to && dates.to.getTime() !== dates.from.getTime()) {
          // Only update form state when we have a complete range to prevent render issues
          const updates = {
            startDate: format(dates.from, 'yyyyMMdd'),
            endDate: format(dates.to, 'yyyyMMdd'),
          }

          const result = setHotels(watchHotels, updates, index)
          setValue('hotels', result, { shouldDirty: true })
          setActiveHotelState((prev) => ({
            ...prev,
            [hotelId]: {
              isSelectingStart: false,
              isSelectingEnd: false,
            },
          }))
        } else {
          // Only from date - update visual state but don't update form state yet
          setActiveHotelState((prev) => ({
            ...prev,
            [hotelId]: {
              isSelectingStart: false,
              isSelectingEnd: true,
              tempStartDate: dates.from,
            },
          }))
        }
      }
    },
    [setValue, watchHotels, activeHotelState],
  )

  const currentDate = new Date()
  const getSelectedDates = useCallback(
    (hotel: HotelDetail) => {
      const hotelState = activeHotelState[hotel.id]

      const fromDate =
        hotelState?.tempStartDate ||
        (hotel.startDate ? parse(hotel.startDate, 'yyyyMMdd', new Date()) : undefined)

      const toDate =
        hotelState?.tempEndDate ||
        (hotel.endDate && hotel.endDate !== hotel.startDate
          ? parse(hotel.endDate, 'yyyyMMdd', new Date())
          : undefined)

      return { from: fromDate, to: toDate }
    },
    [activeHotelState],
  )

  const onItemSelect = useCallback(
    (item: LOCATION_SEARCH_TYPE | null, index: number) => {
      const result = setHotels(watchHotels, { endLocation: item }, index)
      setValue('hotels', result, { shouldDirty: true })
      if (isMobile && !!item && document.activeElement instanceof HTMLElement) {
        document.activeElement.blur()
      }
    },
    [isMobile, setValue, watchHotels],
  )

  const handleAddHotel = useCallback(() => {
    const result = addDestination(watchHotels)
    if (result) {
      setValue('hotels', result, { shouldDirty: true })
    }
  }, [setValue, watchHotels])

  const handleRemoveHotel = useCallback(
    (index: number) => {
      const result = removeDestination(watchHotels, index)
      setValue('hotels', result, { shouldDirty: true })
      clearErrors(`hotels.${index}`)
    },
    [setValue, clearErrors, watchHotels],
  )

  const handleTravelerChange = useCallback(
    (travelerInfo: ITravelerInfo): void => {
      setValue('travelers', travelerInfo as TravelerState)
    },
    [setValue],
  )

  const handleSubmitHelper = handleSubmit((data: MultiHotelFormState) => {
    handleSubmitForm(data)
    logSearchFormAnalytics({ cguid, pageName, productType: 'multi-hotel', isMobile })
  })

  return (
    <form className='w-auto' aria-label='horizon-multi-hotel-search-form' onSubmit={handleSubmitHelper}>
      <div className={getFormContainerClass(useCompactLayout)}>
        <div className={getGridContainerClass(useCompactLayout)}>
          <div className={getInputClass('repeatFormControls')}>
            <div className={getInputClass('hotelMulti')}>
              {watchHotels?.map((hotel: HotelDetail, index: number) => (
                <div key={`hotel-${hotel.id}`} className={getInputClass('hotelRow')}>
                  <div className={getInputClass('hotelHeader')}>
                    <Span textStyle='body2' className='font-bold'>
                      Hotel {index + 1}
                    </Span>
                    {index > 0 && (
                      <Button
                        type='tertiary'
                        size='sm'
                        buttonType='button'
                        className='**:text-xs min-h-auto **:font-medium'
                        onClick={() => handleRemoveHotel(index)}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                  <div className={getInputClass('hotelInputsGrid')}>
                    <div className='w-full'>
                      <AutocompleteWrapper
                        label='Where to?'
                        isMobile={isMobile}
                        errorMessage={errors?.hotels?.[index]?.endLocation?.message}
                        searchKey={`hotels.${index}.endLocation`}
                        initialSelectedItem={hotel.endLocation as LOCATION_SEARCH_TYPE | undefined}
                        typeAheadEndpoint='hotels'
                        onItemSelect={(itemSelected) => {
                          onItemSelect(itemSelected, index)
                        }}
                        ref={(elem: HTMLInputElement) => {
                          registerTabElement(elem)
                        }}
                      />
                    </div>
                    <div className={getInputClass('datePicker')}>
                      <DatePicker
                        key={`datepicker-${hotel.id}-${hotel.startDate}-${hotel.endDate}`}
                        className='w-full'
                        title='Hotels: Check-in - Check-out'
                        label='Check-in - Check-out'
                        mode='range'
                        numberOfMonths={isMobileComponent ? 13 : 2}
                        defaultMonth={
                          hotel.startDate ? parse(hotel.startDate, 'yyyyMMdd', new Date()) : undefined
                        }
                        handleCalendarChange={(dates: DateRange) => {
                          onCalendarChange(dates, index)
                        }}
                        errorMessage={
                          errors?.hotels?.[index]?.startDate?.message ||
                          errors?.hotels?.[index]?.endDate?.message
                        }
                        inputRef={registerTabElement}
                        selected={getSelectedDates(hotel)}
                        disabled={{
                          before: currentDate,
                          after:
                            activeHotelState[index]?.tempStartDate && !activeHotelState[index]?.tempEndDate
                              ? addDays(activeHotelState[index].tempStartDate, 28)
                              : undefined,
                        }}
                        startMonth={currentDate}
                        endMonth={addMonths(currentDate, 12)}
                        min={1}
                        collisionAvoidance={DEFAULT_COLLISION_AVOIDANCE}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <Button
            size='sm'
            className={getInputClass('addHotelButton')}
            onClick={handleAddHotel}
            disabled={watchHotels?.length > 4}
          >
            Add Another Hotel
          </Button>
          <div className={getInputClass('travelersSubmitContainer')}>
            <div className={getInputClass('travelersWrapper')}>
              <TravelerSelection
                isHotel
                initialValues={initialValues.travelers}
                roomsEnabled
                maxRooms={9}
                maxChildren={8}
                maxTravelers={36}
                ageSelectEnabled
                tooltipErrorMessageId='travelerSelectionError'
                errorMessage={errors?.travelers?.children?.message || errors?.travelers?.rooms?.message || ''}
                onChange={handleTravelerChange}
                collisionAvoidance={DEFAULT_COLLISION_AVOIDANCE}
              />
            </div>
            <Button
              buttonType='submit'
              type='primary'
              size='lg'
              className={cn(getInputClass('submit'), isMobile && 'fixed right-4 bottom-4 left-4')}
            >
              Find Your Hotels
            </Button>
          </div>
        </div>
      </div>
    </form>
  )
}
